import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownItemProps, DropdownProps } from 'semantic-ui-react';
import styled from 'styled-components';

import { FieldLabel } from '../../../styles';
import { listProperties, PropertyWalletItemDTO } from '../../../services/properties';
import { maskZipCode } from '../../../services/masks';

export type LenderInsuranceWalletAddressFields = {
  zipcode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
};

type Props = {
  estateId?: string;
  propertyPublicId?: string;
  onSelect: (publicId: string, address: LenderInsuranceWalletAddressFields) => void;
  onClear: () => void;
};

const getAddressLabel = (item: PropertyWalletItemDTO): string => {
  if (item?.address?.formatted) return item.address.formatted;

  return [
    item?.address?.street,
    item?.address?.number,
    item?.address?.complement,
    item?.address?.neighborhood,
    item?.address?.city,
    item?.address?.state
  ]
    .filter(Boolean)
    .join(', ');
};

export const WalletTag = styled.span`
  display: inline-block;
  font-size: 0.85rem;
  color: #2185d0;
  margin-bottom: 0.5rem;
`;

const LenderInsurancePropertyWalletSearchDropdown = ({
  estateId,
  propertyPublicId,
  onSelect,
  onClear
}: Props): ReactElement | null => {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<PropertyWalletItemDTO[]>([]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!estateId) {
      setItems([]);
      return;
    }

    const timeout = setTimeout(async () => {
      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;
      setIsLoading(true);

      try {
        const response = await listProperties({
          estateId,
          search: search.trim() || undefined,
          page: 1,
          limit: 10
        });

        if (currentRequestId === requestIdRef.current) {
          setItems(response.items || []);
        }
      } catch (_error) {
        if (currentRequestId === requestIdRef.current) {
          setItems([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [estateId, search]);

  const itemsByPublicId = useMemo(() => {
    const hash = new Map<string, PropertyWalletItemDTO>();
    items.forEach(item => {
      if (item?.publicId) hash.set(item.publicId, item);
    });
    return hash;
  }, [items]);

  const options: DropdownItemProps[] = items.map(item => ({
    key: item.publicId,
    value: item.publicId,
    text: item.identification || 'Sem código do imóvel',
    description: getAddressLabel(item)
  }));

  if (!estateId) {
    return null;
  }

  return (
    <div style={{ marginBottom: '16px' }}>
      <FieldLabel>PESQUISAR NA CARTEIRA DE IMÓVEIS</FieldLabel>
      <Dropdown
        fluid
        selection
        clearable
        search
        loading={isLoading}
        options={options}
        value={propertyPublicId || ''}
        noResultsMessage="Nenhum imóvel encontrado."
        placeholder="Busque por CEP, bairro, logradouro ou código do imóvel"
        onSearchChange={(_event, data: DropdownProps) => {
          setSearch(String(data.searchQuery || ''));
        }}
        onChange={(_event, data: DropdownProps) => {
          const selectedId = String(data.value || '');

          if (!selectedId) {
            onClear();
            return;
          }

          const selected = itemsByPublicId.get(selectedId);
          if (!selected) return;

          onSelect(selected.publicId, {
            zipcode: maskZipCode(selected?.address?.zipcode || ''),
            street: selected?.address?.street || '',
            number: selected?.address?.number || '',
            complement: selected?.address?.complement || '',
            neighborhood: selected?.address?.neighborhood || '',
            city: selected?.address?.city || '',
            state: selected?.address?.state || ''
          });
        }}
      />
    </div>
  );
};

export default LenderInsurancePropertyWalletSearchDropdown;
